import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import ErrorBoundary from "./components/ErrorBoundary";

// Customer Pages
import HomePage from "./pages/HomePage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import MyOrdersPage from "./pages/MyOrdersPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminStock from "./pages/admin/AdminStock";
import AdminFinancial from "./pages/admin/AdminFinancial";
import AdminPurchases from "./pages/admin/AdminPurchases";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminQrPix from "./pages/admin/AdminQrPix";
import AdminHomeBanner from "./pages/admin/AdminHomeBanner";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminAuditLogs from "./pages/admin/AdminAuditLogs";
import AdminChangePassword from "./pages/admin/AdminChangePassword";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";

// Components
import WhatsAppFAB from "./components/WhatsAppFAB";
import AIAssistant from "./components/AIAssistant";

// Protected Route for customers
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Protected Route for admins
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user || user.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <CartProvider>
              <Routes>
                {/* Public Customer Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/produto/:id" element={<ProductPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/cadastro" element={<RegisterPage />} />
                
                {/* Protected Customer Routes */}
                <Route path="/carrinho" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
                <Route path="/pedido/:id" element={<ProtectedRoute><OrderConfirmationPage /></ProtectedRoute>} />
                <Route path="/meus-pedidos" element={<ProtectedRoute><MyOrdersPage /></ProtectedRoute>} />
                
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="/admin/produtos" element={<AdminRoute><AdminProducts /></AdminRoute>} />
                <Route path="/admin/categorias" element={<AdminRoute><AdminCategories /></AdminRoute>} />
                <Route path="/admin/pedidos" element={<AdminRoute><AdminOrders /></AdminRoute>} />
                <Route path="/admin/estoque" element={<AdminRoute><AdminStock /></AdminRoute>} />
                <Route path="/admin/financeiro" element={<AdminRoute><AdminFinancial /></AdminRoute>} />
                <Route path="/admin/compras" element={<AdminRoute><AdminPurchases /></AdminRoute>} />
                <Route path="/admin/configuracoes" element={<AdminRoute><AdminSettings /></AdminRoute>} />
                <Route path="/admin/qr-pix" element={<AdminRoute><AdminQrPix /></AdminRoute>} />
                <Route path="/admin/banner" element={<AdminRoute><AdminHomeBanner /></AdminRoute>} />
                <Route path="/admin/usuarios" element={<AdminRoute><AdminUsers /></AdminRoute>} />
                <Route path="/admin/logs" element={<AdminRoute><AdminAuditLogs /></AdminRoute>} />
                <Route path="/admin/alterar-senha" element={<AdminRoute><AdminChangePassword /></AdminRoute>} />
                <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
                
                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              
              <WhatsAppFAB />
              <AIAssistant />
              <Toaster position="top-right" richColors />
            </CartProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
